/**
 * MCP Server setup for Power Automate
 */

import {
  createServer,
  type IncomingMessage,
  type ServerResponse,
  type Server as HttpServer,
} from "node:http";
import { randomUUID, createHash, timingSafeEqual } from "node:crypto";
import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
  ListResourcesRequestSchema,
  ListResourceTemplatesRequestSchema,
  ReadResourceRequestSchema,
  ListPromptsRequestSchema,
  GetPromptRequestSchema,
} from "@modelcontextprotocol/sdk/types.js";
import type { Config } from "./config/schema.js";
import { resolveEnvironment } from "./config/loader.js";
import { createAuthProviderFromConfig } from "./auth/wam-broker.js";
import { startTokenKeepalive } from "./auth/token-keepalive.js";
import { FlowManagementApi } from "./api/flow-management.js";
import { ConnectorMetadataApi } from "./api/connector-metadata.js";
import { GraphApi } from "./api/graph-api.js";
import { PowerAppsApi } from "./api/powerapps-api.js";
import { CanvasAuthoringClient } from "./api/canvas-authoring-client.js";
import { PowerPlatformAdminApi } from "./api/power-platform-admin-api.js";
import { PowerPlatformInventoryApi } from "./api/power-platform-inventory-api.js";
import { CopilotEvaluationApi } from "./api/copilot-evaluation-api.js";
import { CopilotStudioExecutionApi } from "./api/copilot-studio-execution-api.js";
import { CopilotAdminApi } from "./api/copilot-admin-api.js";
import { DataverseApi } from "./api/dataverse-api.js";
import { ModelDrivenApi } from "./api/model-driven-api.js";
import { DesktopFlowsApi } from "./api/desktop-flows-api.js";
import { ConnectionsApi } from "./api/connections-api.js";
import { PowerPagesApi } from "./api/powerpages-api.js";
import { findDataverseInstanceUrl } from "./api/dataverse-discovery.js";
import { SchemaCache } from "./cache/schema-cache.js";
import { getToolsForFeatures, executeTool, type ToolContext } from "./tools/index.js";
import {
  getAllResourceTemplates,
  listAllResources,
  readResource,
  type ResourceContext,
} from "./resources/index.js";
import { hasFeature, normalizeFeatures } from "./config/features.js";
import { prompts, getPrompt } from "./prompts/index.js";
import { logger } from "./utils/logger.js";
import { ConfigurationError } from "./utils/errors.js";
import { getInstalledVersion } from "./utils/update-checker.js";
import { SERVER_INSTRUCTIONS } from "./server-instructions.js";

export interface McpServerConfig {
  config: Config;
  transportMode?: "stdio" | "http";
  port?: number;
}

/**
 * Create and configure the MCP server
 */
export async function createMcpServer(serverConfig: McpServerConfig): Promise<{
  server: Server;
  start: () => Promise<void>;
}> {
  const { config } = serverConfig;

  logger.info("Initializing Power Automate MCP server");
  const enabledFeatures = normalizeFeatures(config.features.enabled);
  const featureEnabled = (feature: Parameters<typeof hasFeature>[1]): boolean =>
    hasFeature(enabledFeatures, feature);

  // Resolve default environment
  const defaultEnv = resolveEnvironment(config);
  if (!defaultEnv.id) {
    throw new ConfigurationError(
      "No default environment configured. Add at least one environment to your config.",
    );
  }

  logger.info({ environment: defaultEnv.name, envId: defaultEnv.id }, "Using default environment");

  // Create auth provider (silent-only mode - no interactive prompts in MCP server)
  const authProvider = await createAuthProviderFromConfig(
    config.auth.clientId,
    config.auth.tenantId,
    undefined,
    true, // silentOnly - MCP server can't show interactive dialogs
  );

  // Create API clients
  const flowApi = new FlowManagementApi({
    authProvider,
    defaultEnvironmentId: defaultEnv.id,
  });

  const connectorApi = featureEnabled("powerApps")
    ? new ConnectorMetadataApi({
        authProvider,
        environmentId: defaultEnv.id,
      })
    : null;

  // Create Graph API client for OneDrive/Excel operations
  const graphApi = featureEnabled("graph") ? new GraphApi({ authProvider }) : null;

  // Create Power Apps API client
  const powerAppsApi = featureEnabled("powerApps")
    ? new PowerAppsApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  // Create Power Platform Admin API client
  const needsAdminApi =
    featureEnabled("admin") || featureEnabled("dataverse") || featureEnabled("powerPages");
  const adminApi = needsAdminApi
    ? new PowerPlatformAdminApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  // Tenant-wide Copilot inventory is part of the Dataverse/Copilot tool
  // surface, but authenticates against the Power Platform API audience.
  const powerPlatformInventoryApi = featureEnabled("dataverse")
    ? new PowerPlatformInventoryApi({ authProvider })
    : null;

  const copilotEvaluationApi = featureEnabled("dataverse")
    ? new CopilotEvaluationApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  const copilotStudioExecutionApi = featureEnabled("dataverse")
    ? new CopilotStudioExecutionApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  const copilotAdminApi = featureEnabled("dataverse")
    ? new CopilotAdminApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  // Create Power Pages management API client (api.powerplatform.com)
  // Connection lifecycle rides the Power Platform API audience (same token as
  // Power Pages management); connections are core to flows, so it is always on.
  const connectionsApi = new ConnectionsApi({
    authProvider,
    defaultEnvironmentId: defaultEnv.id,
  });

  const powerPagesApi = featureEnabled("powerPagesManagement")
    ? new PowerPagesApi({
        authProvider,
        defaultEnvironmentId: defaultEnv.id,
      })
    : null;

  // Resolve Dataverse URL.
  // Precedence: (1) config override, (2) BAP admin API (linkedEnvironmentMetadata.instanceUrl),
  // (3) region-based guess. The region guess is correct only when the org's domainName
  // matches the environment GUID — usually it does not, so it is a last resort.
  let dataverseApi: DataverseApi | null = null;
  let modelDrivenApi: ModelDrivenApi | null = null;
  let desktopFlowsApi: DesktopFlowsApi | null = null;
  let bootDataverseUrl: string | null = null;
  try {
    let dataverseUrl: string | undefined;
    let source: "config" | "bap" | "flow-environments" | "fallback" = "fallback";
    const needsDataverseApi = featureEnabled("dataverse") || featureEnabled("powerPages");

    if (defaultEnv.dataverseUrl) {
      dataverseUrl = defaultEnv.dataverseUrl;
      source = "config";
    } else if (adminApi) {
      try {
        const envDetail = await adminApi.getEnvironment(defaultEnv.id);
        const instanceUrl = envDetail.properties?.linkedEnvironmentMetadata?.instanceUrl;
        if (instanceUrl) {
          dataverseUrl = instanceUrl;
          source = "bap";
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        const isAuthError =
          errMsg.includes("cached credentials") || errMsg.includes("authenticate");
        logger.warn(
          { err: errMsg },
          isAuthError
            ? "BAP admin API token not cached — run --setup again or add dataverseUrl to your environment config"
            : "BAP admin API did not return a Dataverse instanceUrl — falling back to region guess (may fail DNS)",
        );
      }
    }

    // The BAP detail endpoint can return 404 for environments that the Flow
    // environment-list endpoint resolves correctly. That list includes the
    // authoritative Dataverse instanceUrl, so use it before guessing a host.
    if (!dataverseUrl && needsDataverseApi) {
      try {
        const environments = await flowApi.listEnvironments();
        const instanceUrl = findDataverseInstanceUrl(environments, defaultEnv.id);
        if (instanceUrl) {
          dataverseUrl = instanceUrl;
          source = "flow-environments";
        }
      } catch (err) {
        logger.warn(
          { err: err instanceof Error ? err.message : String(err) },
          "Flow environment list did not return a Dataverse instanceUrl",
        );
      }
    }

    if (!dataverseUrl && needsDataverseApi) {
      dataverseUrl = DataverseApi.getDataverseUrl(defaultEnv.id, defaultEnv.region);
    }

    if (dataverseUrl && needsDataverseApi) {
      dataverseApi = new DataverseApi({
        authProvider,
        environmentUrl: dataverseUrl,
      });
      modelDrivenApi = new ModelDrivenApi({
        authProvider,
        environmentUrl: dataverseUrl,
      });
      desktopFlowsApi = new DesktopFlowsApi({
        authProvider,
        environmentUrl: dataverseUrl,
      });
      bootDataverseUrl = dataverseUrl;
      logger.info({ dataverseUrl, source }, "Dataverse API initialized");
    }
  } catch (error) {
    logger.warn(
      { error },
      "Failed to initialize Dataverse API — Dataverse tools will return guidance",
    );
  }

  // Initialize schema cache
  let schemaCache: SchemaCache | null = null;
  try {
    schemaCache = new SchemaCache(config.cache.schemaPath ?? undefined);
    logger.info("Schema cache initialized");
  } catch (error) {
    logger.warn({ error }, "Failed to initialize schema cache, continuing without cache");
  }

  // Per-environment config overrides for session switching (switch_environment):
  // an environment pinned in config.json keeps its configured dataverseUrl and
  // region even when visited via a session switch.
  const environmentConfigOverrides: Record<string, { dataverseUrl?: string; region?: string }> = {};
  for (const [key, value] of Object.entries(config.environments)) {
    if (key === "default" || typeof value !== "object" || value === null) continue;
    const entry = value as { id?: string; region?: string; dataverseUrl?: string };
    if (!entry.id) continue;
    const canonical = entry.id
      .split("/")
      .at(-1)!
      .toLowerCase()
      .replace(/^default-/, "");
    environmentConfigOverrides[canonical] = {
      ...(entry.dataverseUrl ? { dataverseUrl: entry.dataverseUrl } : {}),
      ...(entry.region ? { region: entry.region } : {}),
    };
  }

  // Tool context
  const toolContext: ToolContext = {
    auth: {
      clientId: config.auth.clientId,
      tenantId: config.auth.tenantId,
      getPowerPlatformClaimsBinding: () => authProvider.getPowerPlatformClaimsBinding?.() ?? null,
    },
    authProvider,
    environmentSession: {
      bootId: defaultEnv.id,
      bootName: defaultEnv.name,
      currentId: defaultEnv.id,
      currentName: defaultEnv.name,
      dataverseUrl: bootDataverseUrl,
      configOverrides: environmentConfigOverrides,
    },
    flowApi,
    connectorApi,
    graphApi,
    powerAppsApi,
    adminApi,
    powerPlatformInventoryApi,
    copilotEvaluationApi,
    copilotStudioExecutionApi,
    copilotAdminApi,
    dataverseApi,
    modelDrivenApi,
    desktopFlowsApi,
    connectionsApi,
    powerPagesApi,
    cache: schemaCache,
    defaultEnvironmentId: defaultEnv.id,
    enabledFeatures,
  };

  // Resource context
  const resourceContext: ResourceContext = {
    flowApi,
    connectorApi,
    cache: schemaCache,
    defaultEnvironmentId: defaultEnv.id,
    enabledFeatures,
  };

  // Build a fully-wired MCP Server instance. The SDK's Server supports exactly one
  // connected transport, so HTTP mode calls this once per session (#10) — stdio mode
  // once total. API clients/contexts are shared: they're stateless per request and
  // all sessions act as the same signed-in user.
  const buildServer = (): Server => {
    // The official Canvas child keeps mutable "current app" state after connect.
    // Give every outer MCP session its own lazy child client so HTTP sessions can
    // never switch or compile another session's app. No process starts until a
    // Canvas authoring tool is actually called.
    const canvasAuthoringClient = featureEnabled("powerApps") ? new CanvasAuthoringClient() : null;
    const sessionToolContext: ToolContext = {
      ...toolContext,
      canvasAuthoringClient,
    };
    const server = new Server(
      {
        name: "powerautomate-mcp",
        version: getInstalledVersion(),
      },
      {
        instructions: SERVER_INSTRUCTIONS,
        capabilities: {
          tools: {},
          resources: {},
          prompts: {},
        },
      },
    );

    // Register tool handlers
    // eslint-disable-next-line @typescript-eslint/require-await -- The MCP SDK's setRequestHandler expects a Promise-returning handler.
    server.setRequestHandler(ListToolsRequestSchema, async () => {
      logger.debug("Listing tools");
      return { tools: getToolsForFeatures(enabledFeatures) };
    });

    server.setRequestHandler(CallToolRequestSchema, async (request) => {
      const { name, arguments: args } = request.params;
      logger.info({ tool: name }, "Tool called");

      // Guard against deeply nested objects (DoS via stack exhaustion during serialization)
      if (args && !checkObjectDepth(args, 20)) {
        return {
          content: [
            { type: "text" as const, text: `Error: Input too deeply nested (max 20 levels).` },
          ],
          isError: true,
        };
      }

      // Guard against oversized inputs (DoS protection)
      const inputSize = args ? JSON.stringify(args).length : 0;
      const MAX_INPUT_SIZE = 2 * 1024 * 1024; // 2MB
      if (inputSize > MAX_INPUT_SIZE) {
        return {
          content: [
            {
              type: "text" as const,
              text: `Error: Input too large (${(inputSize / 1024 / 1024).toFixed(1)}MB). Maximum is 2MB.`,
            },
          ],
          isError: true,
        };
      }

      const result = await executeTool(sessionToolContext, name, args);
      return result;
    });

    // Register resource handlers
    // eslint-disable-next-line @typescript-eslint/require-await -- The MCP SDK's setRequestHandler expects a Promise-returning handler.
    server.setRequestHandler(ListResourceTemplatesRequestSchema, async () => {
      logger.debug("Listing resource templates");
      return { resourceTemplates: getAllResourceTemplates(enabledFeatures) };
    });

    server.setRequestHandler(ListResourcesRequestSchema, async () => {
      logger.debug("Listing resources");
      const resources = await listAllResources(resourceContext);
      return { resources };
    });

    server.setRequestHandler(ReadResourceRequestSchema, async (request) => {
      const { uri } = request.params;
      logger.info({ uri }, "Reading resource");

      const result = await readResource(resourceContext, uri);
      if (!result) {
        throw new Error(`Resource not found: ${uri}`);
      }
      return result;
    });

    // Register prompt handlers
    // eslint-disable-next-line @typescript-eslint/require-await -- The MCP SDK's setRequestHandler expects a Promise-returning handler.
    server.setRequestHandler(ListPromptsRequestSchema, async () => {
      logger.debug("Listing prompts");
      return { prompts };
    });

    // eslint-disable-next-line @typescript-eslint/require-await -- The MCP SDK's setRequestHandler expects a Promise-returning handler.
    server.setRequestHandler(GetPromptRequestSchema, async (request) => {
      const { name, arguments: args } = request.params;
      logger.info({ prompt: name }, "Getting prompt");

      const result = getPrompt(name, args ?? {});
      if (!result) {
        throw new Error(`Unknown prompt: ${name}`);
      }
      return result;
    });

    // Error handling
    server.onerror = (error) => {
      logger.error({ error }, "MCP server error");
    };
    server.onclose = () => {
      void canvasAuthoringClient?.close().catch((error: unknown) => {
        logger.warn(
          { error: error instanceof Error ? error.message : String(error) },
          "Failed to close Canvas authoring child cleanly",
        );
      });
    };

    return server;
  };

  const server = buildServer();

  // Start function — supports stdio and HTTP transports
  async function start(): Promise<void> {
    // Keep the refresh token warm across long-lived sessions: each silent
    // redemption rotates it and resets the 90-day inactivity window, and
    // failures surface in the log as they happen instead of at the user's
    // next command. Unref'd timer — never holds the process open.
    startTokenKeepalive(authProvider);
    if (serverConfig.transportMode === "http") {
      await startHttpTransport(buildServer, { port: serverConfig.port ?? 3000 });
    } else {
      const transport = new StdioServerTransport();
      logger.info("Starting MCP server with stdio transport");
      await server.connect(transport);
      logger.info("MCP server connected and ready");
    }
  }

  return { server, start };
}

/** Options for the Streamable HTTP transport (#10). */
export interface HttpTransportOptions {
  port: number;
  /** Bind host. Default 127.0.0.1 — never expose without an auth token + threat model. */
  host?: string;
  /** Require `Authorization: Bearer <token>` on /mcp. Default: PA_MCP_HTTP_TOKEN env var. */
  authToken?: string;
  /** Concurrent session cap (default 32) — a runaway client gets 503, not OOM. */
  maxSessions?: number;
  /** Skip SIGINT/SIGTERM hooks + startup banner (tests). */
  quiet?: boolean;
}

/** Origins a browser may legitimately send when talking to a local MCP server. */
function isLocalOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "[::1]" ||
      hostname === "::1"
    );
  } catch {
    return false;
  }
}

/** Constant-time bearer comparison (hash first so lengths never differ). */
function bearerMatches(expected: string, authorization: string | undefined): boolean {
  if (!authorization?.startsWith("Bearer ")) return false;
  const a = createHash("sha256").update(expected).digest();
  const b = createHash("sha256").update(authorization.slice(7)).digest();
  return timingSafeEqual(a, b);
}

/**
 * Start the Streamable HTTP MCP transport (used by ChatGPT / remote clients).
 *
 * Hardening (#10):
 * - One MCP `Server` instance per session (the SDK supports one transport per Server;
 *   sharing one instance across sessions cross-routes responses).
 * - Origin validation: browser-originated cross-site requests (DNS rebinding) carry a
 *   foreign Origin header and are rejected; non-browser clients send no Origin.
 * - Optional bearer auth (PA_MCP_HTTP_TOKEN) with constant-time comparison.
 * - Session cap (503 beyond it) and localhost-only binding by default.
 */
export async function startHttpTransport(
  serverFactory: () => Server,
  options: HttpTransportOptions,
): Promise<HttpServer> {
  const host = options.host ?? "127.0.0.1";
  const authToken = options.authToken ?? process.env["PA_MCP_HTTP_TOKEN"];
  const maxSessions = options.maxSessions ?? 32;

  // Map of session ID → transport (the per-session Server is reachable via the transport)
  const transports = new Map<string, StreamableHTTPServerTransport>();

  // Node's request listener is void-returning: an async listener whose promise
  // rejects becomes an unhandled rejection (fatal on modern Node). Handle the
  // request in a named async function and convert any escape into a 500.
  const handleHttpRequest = async (req: IncomingMessage, res: ServerResponse): Promise<void> => {
    const url = req.url ?? "/";

    // Health check endpoint (no auth — no sensitive data)
    if (url === "/health" && req.method === "GET") {
      res.writeHead(200, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ status: "ok", transport: "streamable-http" }));
      return;
    }

    // Only handle /mcp path
    if (url !== "/mcp") {
      res.writeHead(404);
      res.end("Not Found");
      return;
    }

    // DNS-rebinding protection: a browser reaching this server cross-site sends a
    // non-local Origin. Requests without an Origin (CLI/server clients) pass.
    const origin = req.headers.origin;
    if (origin && !isLocalOrigin(origin)) {
      logger.warn({ origin }, "Rejected /mcp request with non-local Origin");
      res.writeHead(403, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: "Forbidden: non-local origin" }));
      return;
    }

    // Optional bearer auth
    if (authToken && !bearerMatches(authToken, req.headers.authorization)) {
      res.writeHead(401, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: "Unauthorized" }));
      return;
    }

    // Check for existing session
    const sessionId = req.headers["mcp-session-id"] as string | undefined;

    if (sessionId && transports.has(sessionId)) {
      // Route to existing transport
      const transport = transports.get(sessionId)!;
      await transport.handleRequest(req, res);
      return;
    }

    // New session — only allow POST for initialization
    if (req.method === "POST") {
      if (transports.size >= maxSessions) {
        logger.warn({ maxSessions }, "HTTP session cap reached — rejecting new session");
        res.writeHead(503, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: `Session limit reached (${maxSessions})` }));
        return;
      }

      const transport = new StreamableHTTPServerTransport({
        sessionIdGenerator: () => randomUUID(),
      });

      transport.onclose = () => {
        const sid = transport.sessionId;
        if (sid) {
          transports.delete(sid);
          logger.info({ sessionId: sid }, "HTTP session closed");
        }
      };

      // Each session gets its own MCP Server instance (#10) — the SDK routes a
      // session's responses through the transport connected to *its* Server.
      await serverFactory().connect(transport);

      // Store transport by session ID after handling the init request
      await transport.handleRequest(req, res);

      const sid = transport.sessionId;
      if (sid) {
        transports.set(sid, transport);
        logger.info({ sessionId: sid }, "New HTTP session created");
      }
      return;
    }

    // GET/DELETE without valid session
    if (sessionId) {
      res.writeHead(404, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: "Session not found" }));
    } else {
      res.writeHead(400, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: "Missing mcp-session-id header" }));
    }
  };

  const httpServer = createServer((req: IncomingMessage, res: ServerResponse) => {
    void handleHttpRequest(req, res).catch((error: unknown) => {
      logger.error({ err: error, url: req.url }, "Unhandled error in HTTP request handler");
      if (!res.headersSent) {
        res.writeHead(500, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: "Internal server error" }));
      } else {
        res.end();
      }
    });
  });

  // Bind to localhost only by default — prevents remote access
  await new Promise<void>((resolve) => {
    httpServer.listen(options.port, host, () => {
      logger.info(
        { port: options.port, host, auth: !!authToken },
        "MCP server listening with Streamable HTTP transport",
      );
      if (!options.quiet) {
        // Print to stderr (not stdout) so it doesn't interfere with MCP
        console.error(`Power Automate MCP server running at http://${host}:${options.port}/mcp`);
      }
      resolve();
    });
  });

  // Graceful shutdown
  const shutdown = () => {
    logger.info("Shutting down HTTP server");
    for (const [sid, transport] of transports) {
      transport.close().catch(() => {});
      transports.delete(sid);
    }
    httpServer.close();
  };

  if (!options.quiet) {
    process.on("SIGINT", shutdown);
    process.on("SIGTERM", shutdown);
  }

  return httpServer;
}

/**
 * Check that an object's nesting depth does not exceed maxDepth.
 * Prevents stack exhaustion during JSON.stringify on deeply nested inputs.
 */
function checkObjectDepth(obj: unknown, maxDepth: number, current = 0): boolean {
  if (current > maxDepth) return false;
  if (obj === null || typeof obj !== "object") return true;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      if (!checkObjectDepth(item, maxDepth, current + 1)) return false;
    }
  } else {
    for (const value of Object.values(obj as Record<string, unknown>)) {
      if (!checkObjectDepth(value, maxDepth, current + 1)) return false;
    }
  }
  return true;
}
